import React from 'react';
import { motion } from 'framer-motion';
import { useMoney } from '../../context/CurrencyContext';
import { NumberStepper } from '../NumberStepper';

const MARKUP_STEPS = [
  { key: 'escalationPct', label: 'Escalation', amtKey: 'escalation', color: 'hsl(var(--teal))' },
  { key: 'contingencyPct', label: 'Contingency', amtKey: 'contingency', color: 'hsl(var(--teal))' },
  { key: 'adminOhPct', label: 'Admin OH', amtKey: 'adminOh', color: 'hsl(var(--teal))' },
];
const PROFIT_STEPS = [
  { key: 'negotiationPct', label: 'Negotiation', amtKey: 'negotiation', color: 'hsl(var(--copper))' },
];

function Bar({ offset, width, color }) {
  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
      <motion.div className="absolute inset-y-0 rounded-full"
        initial={false}
        animate={{ left: `${offset * 100}%`, width: `${width * 100}%` }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        style={{ backgroundColor: color }} />
    </div>
  );
}

function StepRow({ label, pctKey, pctVal, amount, offset, width, color, onPct, editable, testid }) {
  const { money } = useMoney();
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 text-muted-foreground">{label}
          {editable && (
            <NumberStepper value={pctVal} step={0.25} decimals={2} min={0} max={100} suffix="%"
              data-testid={`markup-ladder-step-input-${pctKey}`} inputClassName="w-12"
              onChange={(v) => onPct(pctKey, v)} />
          )}
        </span>
        <span className="money font-medium">{money(amount)}</span>
      </div>
      <Bar offset={offset} width={width} color={color} />
    </div>
  );
}

export function MarkupWaterfall({ computed, markup, onMarkupChange, readOnly = false }) {
  const { money, code } = useMoney();
  const L = computed.ladder;
  const Q = L.quotationPrice || 1;
  const w = (v) => v / Q;
  let cum = 0;
  const directW = w(L.directCost); cum = L.directCost;

  return (
    <div data-testid="workspace-markup-ladder" className="space-y-3">
      {/* Direct cost base */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium">Direct Cost</span>
          <span className="money font-medium">{money(L.directCost)}</span>
        </div>
        <Bar offset={0} width={directW} color="hsl(var(--primary))" />
      </div>

      {MARKUP_STEPS.map((s) => {
        const off = cum / Q; const width = w(L[s.amtKey]); cum += L[s.amtKey];
        return <StepRow key={s.key} label={s.label} pctKey={s.key} pctVal={((markup[s.key] || 0) * 100).toFixed(2)}
          amount={L[s.amtKey]} offset={off} width={width} color={s.color} onPct={onMarkupChange} editable={!readOnly}
          testid={`markup-ladder-step-${s.amtKey}`} />;
      })}

      <div className="flex items-center justify-between border-y py-2 text-xs">
        <span className="font-semibold">Final Cost Price</span>
        <span className="money font-semibold">{money(L.finalCostPrice)}</span>
      </div>

      {/* P5 — Profit is now set PER Sale line; this is the read-only blended result. */}
      {(() => {
        const off = cum / Q; const width = w(L.profit); cum += L.profit;
        const blended = ((computed.blendedProfitPct || 0) * 100).toFixed(2);
        return (
          <div className="space-y-1" data-testid="markup-ladder-step-profit">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-muted-foreground">Profit
                <span data-testid="markup-blended-profit-pct"
                  className="rounded bg-muted px-1.5 py-0.5 font-num text-[10px] text-muted-foreground"
                  title="Blended profit % across all Sale lines — set Profit % per Sale line in the Sale BOQ panel">
                  {blended}% blended
                </span>
              </span>
              <span className="money font-medium">{money(L.profit)}</span>
            </div>
            <Bar offset={off} width={width} color="hsl(var(--copper))" />
          </div>
        );
      })()}

      {PROFIT_STEPS.map((s) => {
        const off = cum / Q; const width = w(L[s.amtKey]); cum += L[s.amtKey];
        return <StepRow key={s.key} label={s.label} pctKey={s.key} pctVal={((markup[s.key] || 0) * 100).toFixed(2)}
          amount={L[s.amtKey]} offset={off} width={width} color={s.color} onPct={onMarkupChange} editable={!readOnly}
          testid={`markup-ladder-step-${s.amtKey}`} />;
      })}

      <div className="rounded-xl bg-[hsl(var(--primary))] px-3 py-2.5 text-[hsl(var(--primary-foreground))]">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wide opacity-90">Quotation Price</span>
          <span className="flex items-center gap-1.5">
            <span className="rounded bg-white/15 px-1.5 py-0.5 font-num text-[10px]" title={`Quotation multiplier ×${L.markupFactor}`}>×{(Number(L.markupFactor) || 0).toFixed(3)}</span>
          </span>
        </div>
        <div className="money mt-0.5 text-xl font-bold" data-testid="quotation-price">{code} {money(L.quotationPrice)}</div>
      </div>
    </div>
  );
}
